import type { Folder } from './api';

/** Add a named, stable structural row for the user's own mailbox without changing API folder IDs. */
export function mailboxNavigationFolders(folders: readonly Folder[], own: { name: string; address: string }): (Folder & { mailboxAddress?: string })[] {
  const firstOwn = folders.findIndex((folder) => !folder.mailboxRoot && !folder.mailboxContext);
  const ownFolders = firstOwn < 0 ? [] : folders.slice(firstOwn).filter((folder) => !folder.mailboxRoot && !folder.mailboxContext);
  const otherFolders = folders.filter((folder) => !ownFolders.includes(folder));
  const root: Folder & { mailboxAddress: string } = { id: 'mailbox:own', name: own.name, depth: 0, unread: 0, total: 0, mailboxRoot: true, mailboxAddress: own.address };
  return [root, ...ownFolders.map((folder) => ({ ...folder, depth: folder.depth + 1 })), ...otherFolders];
}

/** API order is depth-first; reset ancestry when crossing own/shared mailbox boundaries. */
export function folderParents(folders: readonly Folder[]): ReadonlyMap<string, string> {
  const result = new Map<string, string>();
  const stack: Folder[] = [];
  for (const folder of folders) {
    if (folder.mailboxRoot) stack.length = 0;
    else if (!folder.mailboxContext && stack.some((entry) => entry.mailboxContext || entry.mailboxRoot && entry.id !== 'mailbox:own')) stack.length = 0;
    while (stack.length && stack[stack.length - 1]!.depth >= folder.depth) stack.pop();
    const parent = stack[stack.length - 1];
    const parentIsShared = Boolean(parent?.mailboxContext || parent?.mailboxRoot && parent.id !== 'mailbox:own');
    if (parent && parentIsShared === Boolean(folder.mailboxContext)) result.set(folder.id, parent.id);
    stack.push(folder);
  }
  return result;
}

/** Saved IDs are conveniences only: discovery remains the authority for visibility and actions. */
export function visibleFavourites(ids: ReadonlySet<string>, folders: readonly Folder[]): Folder[] {
  return folders.filter((folder) => ids.has(folder.id) && !folder.mailboxRoot);
}

/** Qualify identically named shared favourites with their discovered mailbox group. */
export function folderMailboxName(id: string, folders: readonly Folder[], parents: ReadonlyMap<string, string>): string | undefined {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const seen = new Set<string>();
  let current: string | undefined = id;
  while (current && !seen.has(current)) {
    seen.add(current);
    const folder = byId.get(current);
    if (folder?.mailboxRoot) return folder.name;
    current = parents.get(current);
  }
  return undefined;
}
